import { XIcon } from '@heroicons/react/solid';
import { message, Modal } from 'antd';
import { motion } from 'framer-motion';
import Image from 'next/image';
import { useRouter } from 'next/router';
import { useState } from 'react';
import { useRecoilValue } from 'recoil';

import { normalizeProductName } from '@/utils/index';

import ButtonDefault, { ButtonVariants } from '../../shared/basic/button';
import { Facebook } from '../../../../assets';
import TwitterAppIcon from '../../../../assets/CourseIcon/TwitterAppIcon';
import WhatsAppIcon from '../../../../assets/CourseIcon/WhatsAppIcon';
import LinkedInIcon from '../../../../assets/Icons/LinkedInIcon';
import { user } from '../../../atoms/user';
import { EventDetails, logEvent } from '../../../hooks/useClickEventTracking';
import { User } from '../../../utils/interfaces/User';

interface ShareModalProps {
  open: boolean;
  onClose: () => void;
  name: string;
}

const ShareModal: React.FC<ShareModalProps> = ({ open, onClose, name }) => {
  const userData: User | undefined = useRecoilValue(user);
  const router = useRouter();
  const { id } = router.query;

  const [copied, setCopied] = useState(false);

  const getShareUrl = (): string => {
    const baseUrl =
      process.env.NEXT_PUBLIC_APP_ENV === 'production'
        ? 'https://zebralearn.com/dl'
        : 'https://staging.zebralearn.com/dl';

    const fullUrl = new URL(router.asPath, baseUrl);
    fullUrl.searchParams.append('ref', userData?.ref_code || '');

    return fullUrl.toString();
  };

  const handleShareClick = (platform: string) => {
    const eventParams: EventDetails = {
      event_name: 'share_product',
      source: 'courses',
      source_type: 'courses',
      source_id: id as string,
      source_name: name ? normalizeProductName(name) : name,
      sub_source: null,
      sub_source_id: null,
      unit: 'boolean',
      value: 1,
      meta: {
        click_source: 'course_details_screen',
        click_action: platform,
      },
    };

    logEvent({
      eventDetails: eventParams,
      userData: userData,
      pathname: router.pathname,
    });
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(getShareUrl());
    setCopied(true);
    handleShareClick('copy');
    message.success('Link copied to clipboard!');
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width={600}
      className='customModalNoPadding customLoginModal bg-none'
      closable={false}
      centered
      destroyOnHidden
    >
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        className='p-6 flex flex-col items-center text-white bg-[#1C1C1C] rounded-[16px]'
      >
        <div className='flex justify-between w-full items-center'>
          <h3 className='text-lg font-semibold mb-2'>Share this course</h3>
          <XIcon
            onClick={onClose}
            className='w-5 h-5  text-white  cursor-pointer'
          />
        </div>

        <div className='flex items-center gap-x-4 w-full py-6  rounded-lg'>
          <input
            type='text'
            value={getShareUrl()}
            readOnly
            className='bg-transparent text-white flex-grow  rounded-lg border-[#3C3B3E] border px-4 py-3 outline-none text-sm'
          />

          <ButtonDefault
            size={4}
            variant={ButtonVariants.PRIMARY}
            className='w-min min-w-[110px] h-full p-1'
            onClick={handleCopy}
          >
            <span className='flex  gap-x-2  whitespace-nowrap'>
              {copied ? 'Copied!' : 'Copy'}
            </span>
          </ButtonDefault>
        </div>

        <div className='flex gap-6 mt-2'>
          <motion.a
            whileHover={{ scale: 1.2 }}
            className='w-10 h-10 flex justify-center border border-[#3C3B3E] rounded-full items-center p-2'
            href={`https://www.facebook.com/sharer/sharer.php?u=${getShareUrl()}`}
            target='_blank'
            onClick={() => handleShareClick('facebook')}
          >
            <div className='bg-[#0866FF] rounded-full p-1 w-5 h-5 flex justify-center items-center'>
              <Image src={Facebook} alt='Facebook' width={7} height={7} />
            </div>
          </motion.a>

          <motion.a
            whileHover={{ scale: 1.2 }}
            className='w-10 h-10 flex justify-center border border-[#3C3B3E] rounded-full items-center p-2'
            href={`https://api.whatsapp.com/send?text=${getShareUrl()}`}
            target='_blank'
            onClick={() => handleShareClick('whatsapp')}
          >
            <WhatsAppIcon />
          </motion.a>

          <motion.a
            whileHover={{ scale: 1.2 }}
            className='w-10 h-10 flex justify-center border border-[#3C3B3E] rounded-full items-center p-2'
            href={`https://x.com/intent/tweet?url=${getShareUrl()}`}
            target='_blank'
            onClick={() => handleShareClick('twitter')}
          >
            <TwitterAppIcon />
          </motion.a>

          <motion.a
            whileHover={{ scale: 1.2 }}
            className='w-10 h-10 flex justify-center border border-[#3C3B3E] rounded-full items-center p-2'
            href={`https://www.linkedin.com/sharing/share-offsite/?url=${getShareUrl()}`}
            target='_blank'
            onClick={() => handleShareClick('linkedin')}
          >
            <LinkedInIcon />
          </motion.a>
        </div>
      </motion.div>
    </Modal>
  );
};

export default ShareModal;
